import {
  InvalidTransactionIssue,
  InvalidTransactionResult,
  FinancialAlerts,
  SepaPaymentDTO,
  EvaluationResult
} from '../types/validation';
import { extractRuleName } from '../utils/formatters';
import { evaluateAlerts, generateBatchSummary } from '../utils/validationService';

// Local validation function for CSV payments (accepts array directly)
export function validateSepaPaymentsLocal(
  payments: SepaPaymentDTO[],
  alertsJson?: FinancialAlerts
): { results: InvalidTransactionResult[]; payments: SepaPaymentDTO[] } {
  if (!payments || payments.length === 0) {
    return { results: [], payments: [] };
  }

  // Initialize evaluation results
  const results: EvaluationResult[] = payments.map((payment, index) => {
    const validationMessages: string[] = [];
    let isValid = true;
    let status: 'VALID' | 'INVALID' | 'SUSPICIOUS' = 'VALID';
    
    // Validation rules
    if (payment.amount > 100000) {
      validationMessages.push('Amount exceeds maximum limit of EUR 100,000');
      isValid = false;
      status = 'INVALID';
    }
    
    if (!payment.debtorIban || payment.debtorIban.length < 15) {
      validationMessages.push('Invalid debtor IBAN format');
      isValid = false;
      status = 'INVALID';
    }
    
    if (!payment.creditorIban || payment.creditorIban.length < 15) {
      validationMessages.push('Invalid creditor IBAN format');
      isValid = false;
      status = 'INVALID';
    }
    
    return {
      paymentId: payment.paymentId,
      status: status,
      evaluationNotes: '',
      isValid: isValid,
      validationMessages: validationMessages
    };
  });

  // Apply alert evaluations if alerts are provided
  if (alertsJson && alertsJson.selectedAlerts && alertsJson.selectedAlerts.length > 0) {
    evaluateAlerts(payments, results, alertsJson);
  }

  // Convert EvaluationResult to InvalidTransactionResult format
  const invalidTransactionResults: InvalidTransactionResult[] = results.map((result, index) => {
    const payment = payments[index];
    const issues: InvalidTransactionIssue[] = [];
    
    // Add evaluation notes as issues if present
    if (result.evaluationNotes && result.evaluationNotes.trim().length > 0) {
      issues.push({
        code: 'ALERT',
        message: result.evaluationNotes
      });
    }

    // Add validation messages as issues
    for (const message of result.validationMessages) {
      issues.push({
        code: 'VALIDATION_ERROR',
        message: message,
        fieldPath: message.includes('debtor') ? 'debtorIban' : 
                  message.includes('creditor') ? 'creditorIban' :
                  message.includes('amount') ? 'amount' : undefined
      });
    }

    return {
      endToEndId: payment?.endToEndId,
      paymentId: payment?.paymentId || payment?.endToEndId,
      index: index,
      issues: issues,
      evaluationNotes: result.evaluationNotes,
      status: result.status,
      isValid: result.isValid,
      validationMessages: result.validationMessages,
      // Include payment data directly in result for easier access
      payment: payment ? {
        amount: payment.amount,
        currency: payment.currency,
        debtorName: payment.debtorName,
        creditorName: payment.creditorName,
        debtorIban: payment.debtorIban,
        creditorIban: payment.creditorIban,
        type: payment.type
      } : undefined
    };
  });

  return { results: invalidTransactionResults, payments };
}

// Main validation entry point for CSV data
export async function validateSepaJson(
  payload: SepaPaymentDTO[] | { payments: SepaPaymentDTO[] },
  alertsJson?: FinancialAlerts,
  options?: { endpoint?: string; signal?: AbortSignal }
): Promise<any> {
  // Use local validation instead of backend call
  try {
    const startTime = Date.now();
    
    // Handle both direct array and wrapped format
    const payments = Array.isArray(payload) ? payload : payload.payments;
    
    const { results, payments: validatedPayments } = validateSepaPaymentsLocal(payments, alertsJson);
    const endTime = Date.now();
    
    // Generate batch summary
    const evaluationResults: EvaluationResult[] = results.map(result => ({
      paymentId: result.endToEndId || `payment_${result.index}`,
      status: result.status || 'VALID',
      evaluationNotes: result.evaluationNotes || '',
      isValid: result.isValid !== false,
      validationMessages: result.validationMessages || []
    }));
    
    const summary = generateBatchSummary(validatedPayments, evaluationResults);
    
    // Count valid payments and payments with triggered alerts
    const validPayments = results.filter(r => r.isValid !== false).length;
    // Count payments with triggered alerts (not just invalid payments)
    const invalidPayments = results.filter(r => {
      const ruleTriggered = extractRuleName(r.validationMessages, r.issues);
      return ruleTriggered !== 'N/A';
    }).length;
    
    // Create batch evaluation response
    const batchResponse = {
      batchId: `batch_${Date.now()}`,
      totalPayments: results.length,
      validPayments: validPayments,
      invalidPayments: invalidPayments,
      processedAt: new Date().toISOString(),
      processingTimeMs: endTime - startTime,
      summary: summary,
      results: results,
      payments: validatedPayments // Include payments array for table component
    };
    
    console.log('CSV validation batch response:', batchResponse);
    console.log('Payments array length:', validatedPayments.length);
    return batchResponse;
  } catch (error) {
    console.error('CSV validation error:', error);
    throw new Error(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

